import { assetUrl } from '../Utils/imageUtils';
import { buildInspectionCategoryFields } from './adminInspectionUtils';
import { buildModuleSchema } from './adminSchemaUtils';
import { CRUD_DEFAULTS } from './adminDefaults';

/** @typedef {{ name: string, label: string, type?: string, options?: string[], ref?: string, rows?: number, default?: unknown, showOn?: string, required?: boolean, hint?: string }} FieldDef */

export const FIELD_SCHEMAS = {
  brands: [
    { name: 'name', label: 'Brand Name', type: 'text', required: true, hint: 'e.g. Maruti Suzuki, Tata, Hyundai' },
    {
      name: 'image',
      label: 'Brand Logo',
      type: 'image',
      uploadLabel: 'Upload brand logo',
      hint: 'Paste a logo URL or upload PNG/JPG/SVG',
    },
  ],
  models: [
    { name: 'name', label: 'Model Name', type: 'text', required: true, hint: 'e.g. Swift, Nexon, Creta' },
    { name: 'brand', label: 'Brand', type: 'ref', ref: 'brands', required: true },
  ],
  'body-types': [
    { name: 'name', label: 'Body Type Name', type: 'text', required: true, hint: 'e.g. SUV, Sedan, Hatchback' },
    {
      name: 'image',
      label: 'Body Type Icon',
      type: 'image',
      uploadLabel: 'Upload icon',
      hint: 'Icon shown in filters and listings',
    },
  ],
  'fuel-types': [
    { name: 'name', label: 'Fuel Type', type: 'text', required: true, hint: 'e.g. Petrol, Diesel, CNG, Electric' },
  ],
  transmissions: [
    { name: 'name', label: 'Transmission Type', type: 'text', required: true, hint: 'e.g. Manual, Automatic, AMT, DCT' },
  ],
  seats: [
    { name: 'name', label: 'Seating Capacity', type: 'text', required: true, hint: 'e.g. 5 Seater, 7 Seater' },
  ],
  'car-colors': [
    { name: 'name', label: 'Color Name', type: 'text', required: true, hint: 'e.g. Pearl White, Midnight Black' },
  ],
  features: [
    { name: 'name', label: 'Feature Name', type: 'text', required: true, hint: 'e.g. Sunroof, ABS, 6 Airbags' },
  ],
  states: [
    { name: 'name', label: 'State Name', type: 'text', required: true, hint: 'e.g. Tamil Nadu, Karnataka' },
  ],
  cities: [
    { name: 'name', label: 'City Name', type: 'text', required: true, hint: 'e.g. Chennai, Bengaluru' },
    { name: 'state', label: 'State', type: 'ref', ref: 'states', required: true },
  ],
  'sub-registers': [
    { name: 'name', label: 'Territory Name', type: 'text', required: true, hint: 'Sales area / sub-register name' },
    { name: 'city', label: 'City', type: 'ref', ref: 'cities', required: true },
  ],
  users: [
    { name: 'name', label: 'Full Name', type: 'text' },
    { name: 'email', label: 'Email Address', type: 'email', required: true },
    { name: 'password', label: 'Password', type: 'password', showOn: 'create', hint: 'Required when creating a new user' },
    { name: 'phone', label: 'Phone Number', type: 'tel' },
    { name: 'photo', label: 'Profile Photo URL', type: 'image' },
    {
      name: 'role',
      label: 'Account Role',
      type: 'select',
      options: ['user', 'admin', 'partner'],
      default: 'user',
      hint: 'Admin = full panel access. Partner = partner portal only.',
    },
  ],
  ads: [
    { name: 'duration', label: 'Slide Duration (milliseconds)', type: 'number', default: 5000, hint: 'How long the ad shows, e.g. 5000 = 5 seconds' },
    { name: 'image', label: 'Banner Image URL', type: 'image', required: true },
    { name: 'car', label: 'Linked Car Listing', type: 'ref', ref: 'cars', hint: 'Car page opened when user clicks the ad' },
  ],
  'why-choose-us': [
    { name: 'title', label: 'Title', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea', rows: 3 },
    { name: 'icon', label: 'Icon Key', type: 'text', hint: 'Internal icon identifier (optional)' },
  ],
  cars: [
    { name: '_section_status', label: 'Listing Status', type: 'section' },
    {
      name: 'status',
      label: 'Approval Status',
      type: 'select',
      options: ['approved', 'awaiting', 'rejected', 'draft'],
      default: 'awaiting',
      hint: 'Only Approved cars appear on the public Buy Cars page',
    },

    { name: '_section_options', label: 'Visibility & Options', type: 'section' },
    {
      name: 'home_show',
      label: 'Show on Homepage',
      type: 'boolean',
      default: true,
      hint: 'Include in homepage car sections',
    },
    {
      name: 'testDriveEnabled',
      label: 'Allow Test Drive Bookings',
      type: 'boolean',
      default: true,
      hint: 'Customers can request a test drive for this car',
    },
    {
      name: 'is_modify',
      label: 'Modified Car',
      type: 'boolean',
      default: false,
      hint: 'Show in the modified cars section',
    },
    {
      name: 'is_project',
      label: 'Project Car',
      type: 'boolean',
      default: false,
      hint: 'Show in the project cars section',
    },

    { name: '_section_vehicle', label: 'Vehicle Details', type: 'section' },
    { name: 'brand', label: 'Brand', type: 'ref', ref: 'brands', required: true },
    { name: 'model', label: 'Model', type: 'ref', ref: 'models', required: true },
    { name: 'body_type', label: 'Body Type', type: 'ref', ref: 'body-types' },
    { name: 'fuel_type', label: 'Fuel Type', type: 'ref', ref: 'fuel-types' },
    { name: 'transmission', label: 'Transmission', type: 'ref', ref: 'transmissions' },
    { name: 'seats', label: 'Seats', type: 'ref', ref: 'seats' },
    { name: 'color', label: 'Color', type: 'ref', ref: 'car-colors' },
    { name: 'features', label: 'Features', type: 'refMulti', ref: 'features', hint: 'Select all that apply' },
    { name: 'year', label: 'Manufacturing Year', type: 'number' },
    { name: 'km_driven', label: 'Kilometers Driven', type: 'number' },
    { name: 'tire_wear', label: 'Tire Wear (%)', type: 'number', hint: '0 = new, 100 = fully worn' },

    { name: '_section_pricing', label: 'Price & Location', type: 'section' },
    { name: 'price', label: 'Price (₹)', type: 'number', required: true },
    { name: 'location', label: 'Location / Address', type: 'text', hint: 'Full address or landmark shown on listing' },
    { name: 'state', label: 'State', type: 'ref', ref: 'states' },
    { name: 'city', label: 'City', type: 'ref', ref: 'cities' },
    { name: 'sub_register', label: 'Sales Territory', type: 'ref', ref: 'sub-registers', hint: 'Determines which partner handles this car' },

    { name: '_section_media', label: 'Photos & Videos', type: 'section' },
    { name: 'images', label: 'Car Photos', type: 'lines', hint: 'One image URL per line — upload or paste links' },
    { name: 'youtube', label: 'YouTube Video URL', type: 'url' },
    { name: 'shorts', label: 'YouTube Shorts URL', type: 'url', hint: 'Shown in Car Highlights after listing is Approved. Supports shorts, watch, or embed links.' },

    { name: '_section_inspection', label: 'Inspection Report', type: 'section' },
    {
      name: 'inspection.isCertified',
      label: 'Certified Vehicle',
      type: 'boolean',
      default: false,
      hint: 'Shows the Certified badge and enables protection plan reservation',
    },
    {
      name: 'inspection.overallScore',
      label: 'Overall Score (0–100)',
      type: 'number',
      default: 0,
      hint: 'Auto-calculated from category scores when you save',
    },
    {
      name: 'inspection.totalPoints',
      label: 'Total Check Points',
      type: 'number',
      default: 0,
      hint: 'Auto-calculated from category max points when you save',
    },
    {
      name: 'inspection.passedPoints',
      label: 'Passed Check Points',
      type: 'number',
      default: 0,
      hint: 'Auto-calculated from category scores when you save',
    },
    {
      name: 'inspection.lastInspectedAt',
      label: 'Last Inspected Date',
      type: 'date',
      hint: 'Date the vehicle was last inspected',
    },
    {
      name: 'inspection.summary',
      label: 'Inspection Summary',
      type: 'textarea',
      rows: 3,
      hint: 'Short summary shown at the top of the inspection report',
    },
    {
      name: 'inspection.reportPdf',
      label: 'Inspection PDF URL',
      type: 'url',
      hint: 'Link to the full PDF inspection report',
    },
    {
      name: 'inspection.photos',
      label: 'Inspection Photo URLs',
      type: 'lines',
      rows: 4,
      hint: 'One image URL per line',
    },
    { name: '_section_inspection_categories', label: 'Inspection Categories', type: 'section' },
    ...buildInspectionCategoryFields(),
    {
      name: 'inspection.history',
      label: 'Inspection History',
      type: 'kvLines',
      rows: 5,
      hint: 'One record per line: Label | Value (optional third part: status)',
    },

    { name: '_section_warranty', label: 'Warranty Package', type: 'section' },
    { name: 'warranty.package', label: 'Package Name', type: 'text', hint: 'e.g. Standard, Premium' },
    { name: 'warranty.supportDays', label: 'Support Period (days)', type: 'number', default: 7 },
    {
      name: 'warranty.engineGearbox',
      label: 'Engine & Gearbox Coverage',
      type: 'boolean',
      default: false,
    },
    {
      name: 'warranty.returnEligible',
      label: 'Return Eligible',
      type: 'boolean',
      default: false,
      hint: 'Buyer can request a return within the return window',
    },
    { name: 'warranty.returnDays', label: 'Return Window (days)', type: 'number', default: 0 },
    {
      name: 'warranty.inclusions',
      label: 'Warranty Inclusions',
      type: 'lines',
      rows: 4,
      hint: 'One item per line',
    },
    {
      name: 'warranty.exclusions',
      label: 'Warranty Exclusions',
      type: 'lines',
      rows: 4,
      hint: 'One item per line',
    },

    { name: '_section_stats', label: 'Statistics', type: 'section' },
    { name: 'view_count', label: 'View Count', type: 'number', default: 0, hint: 'Number of times this listing was viewed' },
  ],
  'test-drives': [
    { name: 'status', label: 'Booking Status', type: 'select', options: ['requested', 'assigned', 'confirmed', 'completed', 'cancelled'], default: 'requested' },
    { name: 'car', label: 'Car', type: 'ref', ref: 'cars', required: true },
    { name: 'user', label: 'Customer Account', type: 'ref', ref: 'users' },
    { name: 'partner', label: 'Assigned Partner', type: 'ref', ref: 'partners' },
    { name: 'preferredDate', label: 'Preferred Date', type: 'text', hint: 'e.g. 2025-06-15' },
    { name: 'preferredTime', label: 'Preferred Time', type: 'text', hint: 'e.g. 10:00 AM' },
    { name: 'location', label: 'Meeting Location', type: 'text' },
    { name: 'name', label: 'Customer Name', type: 'text' },
    { name: 'phone', label: 'Customer Phone', type: 'tel' },
    { name: 'notes', label: 'Notes', type: 'textarea', rows: 3 },
    { name: 'reminderOptIn', label: 'Send Reminder', type: 'boolean', default: true },
  ],
  contact: [
    { name: 'hero.title', label: 'Page Title', type: 'text' },
    { name: 'hero.subtitle', label: 'Page Subtitle', type: 'textarea', rows: 2 },
    { name: 'office.name', label: 'Office Name', type: 'text' },
    { name: 'office.address', label: 'Office Address', type: 'textarea', rows: 2 },
    { name: 'email', label: 'Contact Email', type: 'email' },
    { name: 'phone', label: 'Contact Phone', type: 'tel' },
    { name: 'map.location', label: 'Map Location (city name)', type: 'text', hint: 'Used for the embedded map' },
  ],
  privacy: [
    { name: 'hero.title', label: 'Page Title', type: 'text' },
    { name: 'hero.subtitle', label: 'Page Subtitle', type: 'text' },
  ],
  about: [
    { name: 'hero.title', label: 'Page Title', type: 'text' },
    { name: 'hero.subtitle', label: 'Page Subtitle', type: 'textarea', rows: 2 },
    { name: 'company.heading', label: 'Company Section Label', type: 'text' },
    { name: 'company.title', label: 'Company Headline', type: 'text' },
    { name: 'company.description', label: 'Company Description', type: 'textarea', rows: 4 },
    { name: 'company.features', label: 'Company Highlights', type: 'lines', rows: 5, hint: 'One highlight per line' },
    { name: 'features.title', label: 'Features Section Title', type: 'text' },
    { name: 'features.description', label: 'Features Section Description', type: 'textarea', rows: 3 },
    { name: 'cta.title', label: 'Call-to-Action Title', type: 'text' },
    { name: 'cta.description', label: 'Call-to-Action Text', type: 'textarea', rows: 3 },
    { name: 'cta.button', label: 'Call-to-Action Button Label', type: 'text' },
  ],
  hero: [
    { name: '_section_carousel', label: 'Carousel Slides', type: 'section' },
    { name: 'carousel.0.text', label: 'Slide 1 — Caption', type: 'textarea', rows: 2 },
    { name: 'carousel.0.image', label: 'Slide 1 — Image URL', type: 'image' },
    { name: 'carousel.1.text', label: 'Slide 2 — Caption', type: 'textarea', rows: 2 },
    { name: 'carousel.1.image', label: 'Slide 2 — Image URL', type: 'image' },
    { name: '_section_heroType', label: 'Hero Action Buttons', type: 'section' },
    { name: 'heroType.0.name', label: 'Button 1 — Buy Car', type: 'text' },
    { name: 'heroType.1.name', label: 'Button 2 — Sell Car', type: 'text' },
    { name: 'heroType.2.name', label: 'Button 3 — Valuation', type: 'text' },
  ],
  'home-page': [
    { name: 'cardLimit', label: 'Cars Per Section', type: 'number', default: 8, hint: 'Maximum cars shown in each homepage section' },
  ],
  'app-promo': [
    { name: 'title', label: 'Section Title', type: 'text' },
    { name: 'subtitle', label: 'Subtitle', type: 'text' },
    { name: 'description', label: 'Description', type: 'textarea', rows: 3 },
    { name: 'phoneImage', label: 'Phone Mockup Image URL', type: 'image' },
  ],
  header: [
    { name: '_section_logo', label: 'Logo & Search', type: 'section' },
    { name: 'logo.text', label: 'Site Name (internal)', type: 'text', hint: 'Stored for reference — not shown in the header bar' },
    { name: 'logo.alt', label: 'Logo Alt Text', type: 'text', hint: 'Screen-reader only — used on the logo image, not shown on screen' },
    { name: 'search.placeholder', label: 'Search Placeholder', type: 'text' },
    { name: '_section_auth', label: 'Login / Profile Buttons', type: 'section' },
    { name: 'auth.loginLabel', label: 'Login Button Text', type: 'text' },
    { name: 'auth.profileLabel', label: 'Profile Button Text', type: 'text' },
    { name: '_section_mega', label: 'Buy Car Mega Menu', type: 'section' },
    { name: 'megaMenu.buySubTitle', label: 'Buy Menu Subtitle', type: 'text' },
    { name: 'megaMenu.backLabel', label: 'Buy Menu Back Button', type: 'text' },
    { name: '_section_nav', label: 'Navigation Menu Labels', type: 'section' },
    { name: 'navItems.0.label', label: 'Menu 1 — Buy Car', type: 'text' },
    { name: 'navItems.1.label', label: 'Menu 2 — Sell Car', type: 'text' },
    { name: 'navItems.2.label', label: 'Menu 3 — Valuation', type: 'text' },
    { name: 'navItems.3.label', label: 'Menu 4 — Compare', type: 'text' },
    { name: 'navItems.4.label', label: 'Menu 5 — Finance', type: 'text' },
    { name: 'navItems.5.label', label: 'Menu 6 — About Us', type: 'text' },
    { name: 'navItems.6.label', label: 'Menu 7 — Contact', type: 'text' },
  ],
  auth: [],
  profile: [],
  sellcar: [],
  valuation: [],
  compare: [],
  carTrust: [],
  emi: [],
  warranty: [
    { name: 'badge', label: 'Page Badge Text', type: 'text' },
    { name: 'title', label: 'Page Title', type: 'text' },
    { name: 'subtitle', label: 'Page Subtitle', type: 'textarea', rows: 2 },
  ],
  'protection-orders': [
    { name: 'status', label: 'Order Status', type: 'select', options: ['requested', 'confirmed', 'delivered', 'return_window', 'return_requested', 'return_approved', 'completed', 'cancelled'], default: 'requested' },
    { name: 'car', label: 'Car', type: 'ref', ref: 'cars', required: true },
    { name: 'user', label: 'Customer', type: 'ref', ref: 'users' },
    { name: 'planId', label: 'Plan ID', type: 'text' },
    { name: 'planName', label: 'Plan Name', type: 'text' },
    { name: 'returnDays', label: 'Return Window (days)', type: 'number', default: 7 },
    { name: 'supportDays', label: 'Support Period (days)', type: 'number', default: 30 },
    { name: 'engineGearbox', label: 'Engine & Gearbox Coverage', type: 'boolean', default: false },
    { name: 'carPrice', label: 'Car Price (₹)', type: 'number' },
    { name: 'notes', label: 'Internal Notes', type: 'textarea', rows: 3 },
    { name: 'returnDeadline', label: 'Return Deadline', type: 'text' },
    { name: 'deliveredAt', label: 'Delivered Date', type: 'text' },
  ],
};

export const DEFAULT_VALUES = {
  ...CRUD_DEFAULTS,
  cars: {
    ...CRUD_DEFAULTS.cars,
    images: [assetUrl('card1.jpg')],
  },
};

export const getSchema = (module, data = null) => {
  const staticSchema = FIELD_SCHEMAS[module.schemaKey || module.key] || [];
  return buildModuleSchema(module, data, staticSchema);
};

/** Ref fields that filter based on a parent selection in the same form */
export const REF_CASCADE = {
  model: { parentField: 'brand', matchKey: 'brand' },
  city: { parentField: 'state', matchKey: 'state' },
  sub_register: { parentField: 'city', matchKey: 'city' },
};
